import { createFileRoute } from '@tanstack/react-router';
import { z } from 'zod';

import { AdminPageHeader } from '@/components/admin/admin-page-header';
import { ExportCsvButton } from '@/components/admin/managed-spend/export-csv-button';
import { ManagedSpendTable } from '@/components/admin/managed-spend/managed-spend-table';
import { useAdminManagedSpend } from '@/hooks/data/admin/use-managed-spend';
import {
  MANAGED_SPEND_SORT_FIELDS,
  type ManagedSpendSortField,
} from '@/repository/admin/managed-spend';

// A bookmarked sort the backend no longer takes falls back, not the page.
export const searchSchema = z.object({
  sort_by: z
    .enum(
      MANAGED_SPEND_SORT_FIELDS as [
        ManagedSpendSortField,
        ...ManagedSpendSortField[],
      ],
    )
    .catch('last_30_days_usd'),
  sort_dir: z.enum(['asc', 'desc']).catch('desc'),
  offset: z.number().int().nonnegative().catch(0),
});

const LIMIT = 20;

export const Route = createFileRoute('/_authenticated/admin/managed-spend')({
  validateSearch: searchSchema,
  staticData: { title: 'Managed spend' },
  component: AdminManagedSpendPage,
});

function AdminManagedSpendPage(): React.ReactElement {
  const { sort_by, sort_dir, offset } = Route.useSearch();
  const navigate = Route.useNavigate();
  const query = useAdminManagedSpend({
    sort_by,
    sort_dir,
    limit: LIMIT,
    offset,
  });

  return (
    <main>
      <AdminPageHeader
        title="Managed spend"
        description="Ad spend per client from the nightly snapshot, for investor and board figures."
        actions={<ExportCsvButton sort={{ sort_by, sort_dir }} />}
      />
      <p className="text-fg-3 text-fs-12 px-4 pb-4 sm:px-8">
        Windows end on each row&apos;s Through date. To compare with a
        client&apos;s dashboard, pick that custom range there. Total imported
        covers up to 365 days before each account connected, plus every day
        synced since.
      </p>
      <ManagedSpendTable
        rows={query.data?.items ?? []}
        total={query.data?.total ?? 0}
        limit={LIMIT}
        offset={offset}
        isLoading={query.isLoading}
        isError={query.isError}
        sort={{ key: sort_by, dir: sort_dir }}
        onSortChange={(next) =>
          void navigate({
            search: (s) => ({
              ...s,
              sort_by: next.key as ManagedSpendSortField,
              sort_dir: next.dir,
              offset: 0,
            }),
          })
        }
        onPageChange={(newOffset) =>
          void navigate({ search: (s) => ({ ...s, offset: newOffset }) })
        }
      />
    </main>
  );
}
